import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Button, Select, Table } from "../components/admin-ui";
import { useAdminStalls, useSettlementReport, downloadAdminCsv } from "../api/hooks";
import { tokens } from "../styles/tokens";

const { color: T } = tokens;
const rm = (v: string | number) => `RM ${Number(v).toFixed(2)}`;

/**
 * Per-stall gross and net for any date range, read purely from the amounts snapshotted
 * on each order — Pricing Rules, menu prices and stall settings can change later without
 * touching these numbers. gross - deductions = net on every row.
 */
export function SettlementTab() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [stallId, setStallId] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const { data: stallsData } = useAdminStalls();
  const stalls = stallsData?.data ?? [];
  const params = useMemo(() => ({ from, to, stallId: stallId || undefined }), [from, to, stallId]);
  const { data, isLoading } = useSettlementReport(params);
  const report = data?.data;
  const invalid = from > to;

  const exportCsv = async (kind: "settlement" | "order-lines" | "order-bundles") => {
    setBusy(kind);
    try { await downloadAdminCsv(kind, params); } finally { setBusy(null); }
  };

  const col = (title: string, key: string, strong = false) => ({
    title, dataIndex: key, key, align: "right" as const,
    render: (v: string) => <span style={{ fontWeight: strong ? 700 : 500, fontVariantNumeric: "tabular-nums" }}>{rm(v)}</span>,
  });
  const amountCols = [
    col(t("settlement.gross"), "gross"),
    col(t("settlement.stallFee"), "stallFee"),
    col(t("settlement.addonFee"), "addonFee"),
    col(t("settlement.bundleFee"), "bundleFee"),
    col(t("settlement.bundleShare"), "bundleShare"),
    col(t("settlement.net"), "net", true),
  ];

  const field = { height: 38, borderRadius: 10, border: "1px solid #E5E7EB", padding: "0 10px", fontSize: 14, background: T.surface, color: T.textDark } as const;

  return (
    <div className="admin-report-tab">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: T.textMuted }}>
          {t("settlement.from")}
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={field} />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: T.textMuted }}>
          {t("settlement.to")}
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={field} />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: T.textMuted, minWidth: 200 }}>
          {t("settlement.stall")}
          <select value={stallId} onChange={(e) => setStallId(e.target.value)} style={field}>
            <option value="">{t("settlement.allStalls")}</option>
            {stalls.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
          <Button loading={busy === "settlement"} disabled={invalid} onClick={() => void exportCsv("settlement")}>{t("settlement.exportSettlement")}</Button>
          <Button loading={busy === "order-lines"} disabled={invalid} onClick={() => void exportCsv("order-lines")}>{t("settlement.exportLines")}</Button>
          <Button loading={busy === "order-bundles"} disabled={invalid} onClick={() => void exportCsv("order-bundles")}>{t("settlement.exportBundles")}</Button>
        </div>
      </div>
      {invalid && <div style={{ color: "#DC2626", fontSize: 13, marginBottom: 12 }}>{t("settlement.invalidRange")}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        {[
          [t("settlement.orders"), report ? String(report.totals.orderCount) : "—"],
          [t("settlement.gross"), report ? rm(report.totals.gross) : "—"],
          [t("settlement.deductions"), report ? rm(report.totals.deductions) : "—"],
          [t("settlement.net"), report ? rm(report.totals.net) : "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ background: T.surface, border: "1px solid #E5E7EB", borderRadius: 12, padding: "12px 16px" }}>
            <div style={{ fontSize: 12, color: T.textMuted }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.textDark, fontVariantNumeric: "tabular-nums" }}>{value}</div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: T.textMuted, margin: "0 0 16px" }}>{t("settlement.formulaHint")}</p>

      <h3 style={{ fontSize: 15, margin: "0 0 8px" }}>{t("settlement.byStall")}</h3>
      <Table
        size="small" rowKey="stallId" loading={isLoading} dataSource={report?.stalls ?? []}
        columns={[
          { title: t("settlement.stall"), dataIndex: "stallName", key: "stallName" },
          { title: t("settlement.orders"), dataIndex: "orderCount", key: "orderCount", align: "right" as const },
          ...amountCols,
        ]}
      />

      <h3 style={{ fontSize: 15, margin: "20px 0 8px" }}>{t("settlement.byOrder")}</h3>
      <Table
        size="small" rowKey={(r: { orderId: string; stallId: string }) => `${r.orderId}|${r.stallId}`} loading={isLoading}
        dataSource={report?.orders ?? []}
        columns={[
          { title: t("settlement.orderCode"), dataIndex: "orderCode", key: "orderCode" },
          { title: t("settlement.orderTime"), dataIndex: "createdAt", key: "createdAt", render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm") },
          { title: t("settlement.stall"), dataIndex: "stallName", key: "stallName" },
          ...amountCols,
        ]}
      />
    </div>
  );
}
